import React, { useState } from 'react';
import ModalEdicionContrato from '../../../ui/ModalEdicionContrato';
import ModalAnulacionContratos from '../../../ui/ModalAnulacionContratos';
import { getMotivoChipClass, getPersonaChipClass } from '../../../../utils/styles'; 

/**
 * DetalleTabContrato - Visualización de la ficha técnica del contrato ART.
 * Sincronizado con los campos de FormularioArt (Snapshots de nombres).
 * Corregido: Se subsanó el error de sintaxis SVG en el atributo 'd' de la sección 3.
 */
const DetalleTabContrato = ({ contrato, client, onUpdate }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAnulacionModal, setShowAnulacionModal] = useState(false);

    // Helpers
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        if (typeof dateString === 'string' && dateString.includes('-')) {
            const parts = dateString.split('-');
            if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateString;
    };

    const formatMoney = (val) => {
        if (!val && val !== 0) return '$0,00';
        // Limpieza robusta de strings de moneda si fuera necesario
        const num = typeof val === 'string' ? parseFloat(val.replace(/\./g, '').replace(',', '.')) : val;
        return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(num || 0);
    };

    // Helper para mostrar porcentajes con coma
    const formatPercent = (value) => {
        if (value === undefined || value === null || value === '') return '';
        // Convierte el punto en coma para la visualización (ej: 3.5 -> "3,5")
        return String(value).replace('.', ',');
    };

    const DetailItem = ({ label, value, highlight = false }) => (
        <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-base font-medium break-words min-h-[1.5rem] ${highlight ? 'text-indigo-700 font-bold' : 'text-gray-800'}`}>
                {value || <span className="text-gray-300 italic font-normal text-sm">Sin dato</span>}
            </p>
        </div>
    );

    const isAnulada = contrato.estadoPoliza === 'Anulada' || contrato.estadoPoliza === 'ANULADA';

    return (
        <div className="space-y-6 animate-fade-in text-left">
            
            {/* BOTONES DE ACCIÓN FLOTANTES */}
            <div className="flex justify-end gap-3">
                
                <button 
                    onClick={() => setShowEditModal(true)}
                    disabled={isAnulada}
                    className={`flex items-center text-sm font-semibold px-4 py-2 rounded-full transition-colors shadow-sm border ${isAnulada ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-indigo-50 text-indigo-600 border-indigo-100 hover:bg-indigo-100 hover:text-indigo-800'}`}
                >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg>
                    Editar Contrato
                </button>

                {!isAnulada && (
                    <button 
                        onClick={() => setShowAnulacionModal(true)}
                        className="flex items-center text-sm font-semibold px-4 py-2 rounded-full transition-colors shadow-sm border bg-red-50 text-red-600 border-red-100 hover:bg-red-100 hover:text-red-800"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                        Anular Contrato
                    </button>
                )}
            </div>

            {/* SECCIÓN 0: DATOS DE ANULACIÓN (Solo si está anulada) */}
            {isAnulada && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 relative animate-pulse-once">
                     <div className="flex items-center mb-4 pb-2 border-b border-red-200">
                        <div className="p-2 bg-red-100 rounded-lg text-red-600 mr-3">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                        </div>
                        <h3 className="text-lg font-bold text-red-800">Datos de Anulación</h3>
                    </div>
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <DetailItem label="Motivo Anulación" value={contrato.motivoAnulacion} />
                        <DetailItem label="Fecha Anulación" value={formatDate(contrato.fechaAnulacion)} />
                        <DetailItem label="¿Quién Gestionó?" value={contrato.quienGestionoAnulacion} />
                        <div className="md:col-span-3">
                             <DetailItem label="Observaciones" value={contrato.observacionesAnulacion} />
                        </div>
                     </div>
                </div>
            )}

            {/* SECCIÓN 1: DETALLES DE EMISIÓN */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center mb-6 pb-2 border-b border-gray-100">
                    <div className="p-2 bg-gray-100 rounded-lg text-gray-600 mr-3">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Detalles de la Emisión</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                    <DetailItem label="Fecha de Carga" value={formatDate(contrato.fechaCarga)} />
                    <DetailItem label="N° de Contrato" value={contrato.nContrato} highlight />
                    
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Tipo Operación</p>
                        <div className="mt-1">
                            {contrato.tipoOperacion ? (
                                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${getMotivoChipClass(contrato.tipoOperacion)}`}>
                                    {contrato.tipoOperacion}
                                </span>
                            ) : <span className="text-gray-300 italic text-sm">Sin dato</span>}
                        </div>
                    </div>

                    <DetailItem label="Inicio Vigencia" value={formatDate(contrato.inicioVigencia)} />
                    <DetailItem label="Fin Vigencia" value={formatDate(contrato.finVigencia)} />
                </div>
            </div>

            {/* SECCIÓN 2: DETALLES DEL CONTRATO (ART) */}
            <div className="bg-white p-6 rounded-xl border border-orange-100 shadow-sm relative overflow-hidden text-left">
                <div className="absolute top-0 left-0 w-1.5 h-full bg-orange-400"></div>
                <div className="flex items-center mb-6 pb-2 border-b border-orange-100">
                    <div className="p-2 bg-orange-100 rounded-lg text-orange-600 mr-3">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Detalles del Contrato (ART)</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <DetailItem label="CIIU" value={contrato.ciiu} />
                    <DetailItem label="Cápitas (Empleados)" value={contrato.capitas} />
                    <DetailItem label="Masa Salarial" value={formatMoney(contrato.masaSalarial)} />
                    <DetailItem label="Alícuota" value={contrato.alicuota ? `${formatPercent(contrato.alicuota)}%` : ''} />
                </div>
            </div>

            {/* SECCIÓN 3: DATOS DE LA COMPAÑÍA (CORREGIDO) */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center mb-6 pb-2 border-b border-gray-100">
                    <div className="p-2 bg-gray-100 rounded-lg text-gray-600 mr-3">
                        {/* Se corrigió la sintaxis de 'd' de Google/Heroicons (V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16) */}
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                        </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Datos de la Compañía</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <DetailItem label="Compañía" value={contrato.compania} highlight />
                    <DetailItem label="Organizador" value={contrato.organizador} />
                    <DetailItem label="PAS" value={contrato.pas} />
                </div>
            </div>

            {/* SECCIÓN 4: EQUIPO COMERCIAL */}
            <div className="bg-white p-6 rounded-xl border border-indigo-100 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1.5 h-full bg-indigo-400"></div>
                <div className="flex items-center mb-6 pb-2 border-b border-indigo-100">
                    <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600 mr-3">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Equipo Comercial & Comisiones</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                    <DetailItem label="Canal" value={contrato.canal} />
                    <DetailItem label="Nombre del Canal" value={contrato.canalNombre} />
                    <DetailItem label="Subcanal Asociado" value={contrato.subcanalNombre} />
                    
                    <div className="col-span-1 md:col-span-3 border-t border-dashed border-indigo-100 my-2"></div>

                    {/* Vendedor con Chip */}
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Vendedor</p>
                        <div className="mt-1">
                             {contrato.vendedor ? <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${getPersonaChipClass(contrato.vendedor)}`}>{contrato.vendedor}</span> : '-'}
                        </div>
                    </div>

                    {/* Suscriptor con Chip */}
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Suscriptor</p>
                        <div className="mt-1">
                             {contrato.suscriptor ? <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${getPersonaChipClass(contrato.suscriptor)}`}>{contrato.suscriptor}</span> : '-'}
                        </div>
                    </div>
                    
                    <DetailItem label="Tipo de Conversión" value={contrato.tipoConversion} />
                    
                    <div className="col-span-1 md:col-span-3">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">¿Contador Asociado?</p>
                        {contrato.contadorNombre ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                {contrato.contadorNombre}
                            </span>
                        ) : (
                            <span className="text-gray-400 text-sm italic">No asignado</span>
                        )}
                    </div>
                </div>
            </div>

            {/* SECCIÓN 5: VALORES DEL CONTRATO */}
            <div className="bg-white p-6 rounded-xl border border-green-100 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1.5 h-full bg-green-400"></div>
                <div className="flex items-center mb-6 pb-2 border-b border-green-100">
                    <div className="p-2 bg-green-100 rounded-lg text-green-600 mr-3">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Valores del Contrato</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <DetailItem label="Prima Emitida" value={formatMoney(contrato.primaEmitida)} highlight />
                    <DetailItem label="% Comisión Total (s/Acuerdo)" value={contrato.porcentajeComision ? `${formatPercent(contrato.porcentajeComision)}%` : ''} />
                </div>
            </div>

            {/* SECCIÓN 6: DESPACHO */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm text-left">
                <div className="flex items-center mb-6 pb-2 border-b border-gray-100">
                    <div className="p-2 bg-gray-100 rounded-lg text-gray-600 mr-3">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Despacho</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <DetailItem label="Fecha Despacho" value={formatDate(contrato.fechaDespacho)} />
                    <DetailItem label="Forma Despacho" value={contrato.formaDespacho} />
                </div>
            </div>

            {/* MODAL DE EDICIÓN */}
            <ModalEdicionContrato 
                show={showEditModal}
                onClose={() => setShowEditModal(false)}
                contrato={contrato}
                clientData={client}
                onSuccess={(msg) => {
                    if (onUpdate) onUpdate(msg); 
                }}
            />
            
            {/* MODAL DE ANULACIÓN */}
            <ModalAnulacionContratos
                show={showAnulacionModal}
                onClose={() => setShowAnulacionModal(false)}
                contrato={contrato}
                clientId={client?.id}
                onSuccess={(msg) => {
                    if (onUpdate) onUpdate(msg);
                }}
            />

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default DetalleTabContrato;